import { useEffect, useMemo, useState } from 'react';
import {
  buildTC6AutoInterpretation,
  computeTC6PrimaryReference,
  getTC6ReferenceLabel,
  recalculateTc6Payload,
  TC6_REFERENCE_OPTIONS,
  type Tc6AttemptPayload,
  type Tc6ReferenceKey,
  type Tc6ResultPayload,
} from '../tc6/tc6Domain';

interface TC6ResultEditorProps {
  payload: Tc6ResultPayload | null | undefined;
  saving?: boolean;
  onCancel: () => void;
  onSave: (payload: Tc6ResultPayload) => void;
}

const asInputValue = (value: unknown) => (value == null ? '' : String(value));

const formatMetric = (value: number | null | undefined, unit?: string, digits = 1) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const formatted = value.toFixed(digits);
  return unit ? `${formatted} ${unit}` : formatted;
};

const formatDelta = (start: unknown, end: unknown) => {
  const startNumber = typeof start === 'number' ? start : Number.parseFloat(String(start ?? ''));
  const endNumber = typeof end === 'number' ? end : Number.parseFloat(String(end ?? ''));
  if (Number.isNaN(startNumber) || Number.isNaN(endNumber)) return '—';
  const delta = endNumber - startNumber;
  return `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`;
};

function TC6AttemptEditor({
  label,
  attempt,
  selectedReferenceKey,
  onChange,
}: {
  label: string;
  attempt: Tc6AttemptPayload | Record<string, any> | null | undefined;
  selectedReferenceKey: Tc6ReferenceKey;
  onChange: (updater: (current: Record<string, any>) => Record<string, any>) => void;
}) {
  if (!attempt) return null;

  const perMinute = Array.isArray(attempt.per_minute) ? attempt.per_minute : [];
  const distances = Array.isArray(attempt.per_minute_distance_m) ? attempt.per_minute_distance_m : [];
  const speeds = Array.isArray(attempt.per_minute_speed_kmh) ? attempt.per_minute_speed_kmh : [];
  const derived = attempt.derivedMetrics ?? null;
  const patient = attempt.patientSnapshot ?? {};
  const primaryReference = computeTC6PrimaryReference(attempt as Tc6AttemptPayload, selectedReferenceKey);
  const stopLines = attempt.stop_report?.intervals ?? [];

  const updateMinute = (index: number, field: 'hr' | 'spo2') => (value: string) => {
    onChange((current) => {
      const nextPerMinute = Array.isArray(current.per_minute) ? [...current.per_minute] : [];
      const currentMinute = nextPerMinute[index] && typeof nextPerMinute[index] === 'object' ? nextPerMinute[index] : { minute: index + 1 };
      nextPerMinute[index] = {
        ...currentMinute,
        minute: index + 1,
        [field]: value || null,
      };
      return {
        ...current,
        per_minute: nextPerMinute,
      };
    });
  };

  const updateDistance = (index: number, value: string) => {
    onChange((current) => {
      const nextDistances = Array.isArray(current.per_minute_distance_m) ? [...current.per_minute_distance_m] : [];
      nextDistances[index] = value ? Number(value) : null;
      return {
        ...current,
        per_minute_distance_m: nextDistances,
      };
    });
  };

  const updateBaselineFinal = (
    block: 'baseline' | 'final',
    field: 'hr' | 'spo2',
    value: string,
  ) => {
    onChange((current) => ({
      ...current,
      [block]: {
        ...(current[block] || {}),
        [field]: value || null,
      },
    }));
  };

  const updateBloodPressure = (
    block: 'baseline' | 'final',
    field: 'sys' | 'dia',
    value: string,
  ) => {
    onChange((current) => ({
      ...current,
      [block]: {
        ...(current[block] || {}),
        bp: {
          ...(current[block]?.bp || {}),
          [field]: value || null,
        },
      },
    }));
  };

  const updateBorg = (
    block: 'baseline' | 'final',
    field: 'dyspnea' | 'fatigue',
    value: string,
  ) => {
    onChange((current) => ({
      ...current,
      [block]: {
        ...(current[block] || {}),
        borg: {
          ...(current[block]?.borg || {}),
          [field]: value ? Number(value) : null,
        },
      },
    }));
  };

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
          <div className="mt-1 text-sm text-slate-600">{getTC6ReferenceLabel(selectedReferenceKey)}</div>
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
          {stopLines.length ? `${stopLines.length} parada(s)` : 'Sem paradas'}
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <label className="text-sm font-medium text-slate-700">
          Distância total (m)
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(attempt.total_distance_m)}
            onChange={(event) => onChange((current) => ({ ...current, total_distance_m: event.target.value ? Number(event.target.value) : null }))}
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Comprimento do corredor (m)
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(attempt.corridorLength_m)}
            onChange={(event) => onChange((current) => ({ ...current, corridorLength_m: event.target.value ? Number(event.target.value) : null }))}
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Voltas
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(attempt.laps)}
            onChange={(event) => onChange((current) => ({ ...current, laps: event.target.value ? Number(event.target.value) : null }))}
          />
        </label>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Predito</div>
          <div className="mt-2 text-lg font-bold text-slate-900">
            {primaryReference?.predicted != null ? `${primaryReference.predicted.toFixed(1)} m` : '—'}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {primaryReference?.percentPredicted != null ? `${primaryReference.percentPredicted.toFixed(1)}% do previsto` : getTC6ReferenceLabel(selectedReferenceKey)}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Velocidade média</div>
          <div className="mt-2 text-lg font-bold text-slate-900">{formatMetric(derived?.meanSpeed_kmh ?? null, 'km/h', 2)}</div>
          <div className="mt-1 text-xs text-slate-500">{formatMetric(derived?.distancePerMinute_m ?? null, 'm/min', 1)}</div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Trabalho no TC6</div>
          <div className="mt-2 text-lg font-bold text-slate-900">{formatMetric(derived?.sixMinuteWork_kgm ?? null, 'kg·m', 0)}</div>
          <div className="mt-1 text-xs text-slate-500">{formatMetric(derived?.sixMinuteWork_kJ ?? null, 'kJ', 1)}</div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">VO2 estimado</div>
          <div className="mt-2 text-lg font-bold text-slate-900">{formatMetric(derived?.vo2Estimate_mlkgmin ?? null, 'mL/kg/min', 1)}</div>
          <div className="mt-1 text-xs text-slate-500">{formatMetric(derived?.metsEstimate ?? null, 'METs', 1)}</div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <label className="text-sm font-medium text-slate-700">
          Idade
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(patient.age)}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                patientSnapshot: {
                  ...(current.patientSnapshot || {}),
                  age: event.target.value ? Number(event.target.value) : null,
                },
              }))
            }
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Sexo
          <select
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={patient.sex ?? ''}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                patientSnapshot: {
                  ...(current.patientSnapshot || {}),
                  sex: event.target.value || null,
                },
              }))
            }
          >
            <option value="">Selecione</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
          </select>
        </label>
        <label className="text-sm font-medium text-slate-700">
          Peso (kg)
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(patient.weight)}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                patientSnapshot: {
                  ...(current.patientSnapshot || {}),
                  weight: event.target.value ? Number(event.target.value) : null,
                },
              }))
            }
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Altura (cm)
          <input
            type="number"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={asInputValue(patient.height)}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                patientSnapshot: {
                  ...(current.patientSnapshot || {}),
                  height: event.target.value ? Number(event.target.value) : null,
                },
              }))
            }
          />
          <div className="mt-1 text-xs text-slate-500">IMC recalculado: {formatMetric(patient.bmi ?? null, 'kg/m²', 1)}</div>
        </label>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Min</th>
              <th className="px-3 py-2 text-left">SpO2</th>
              <th className="px-3 py-2 text-left">FC</th>
              <th className="px-3 py-2 text-left">Dist. acum. (m)</th>
              <th className="px-3 py-2 text-left">Vel. (km/h)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {Array.from({ length: 6 }, (_, index) => {
              const minute = perMinute[index] ?? { minute: index + 1, hr: null, spo2: null };
              return (
                <tr key={`${label}-minute-${index}`}>
                  <td className="px-3 py-2 font-semibold text-slate-700">{index + 1}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-2 py-1"
                      value={asInputValue(minute.spo2)}
                      onChange={(event) => updateMinute(index, 'spo2')(event.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-2 py-1"
                      value={asInputValue(minute.hr)}
                      onChange={(event) => updateMinute(index, 'hr')(event.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-2 py-1"
                      value={asInputValue(distances[index])}
                      onChange={(event) => updateDistance(index, event.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {typeof speeds[index] === 'number' ? speeds[index].toFixed(2) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Parâmetro</th>
              <th className="px-3 py-2 text-left">Início</th>
              <th className="px-3 py-2 text-left">Final</th>
              <th className="px-3 py-2 text-left">Variação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {[
              { label: 'SpO2 (%)', start: attempt.baseline?.spo2, end: attempt.final?.spo2, render: (block: 'baseline' | 'final', value: string) => updateBaselineFinal(block, 'spo2', value) },
              { label: 'FC (bpm)', start: attempt.baseline?.hr, end: attempt.final?.hr, render: (block: 'baseline' | 'final', value: string) => updateBaselineFinal(block, 'hr', value) },
              { label: 'PA sistólica', start: attempt.baseline?.bp?.sys, end: attempt.final?.bp?.sys, render: (block: 'baseline' | 'final', value: string) => updateBloodPressure(block, 'sys', value) },
              { label: 'PA diastólica', start: attempt.baseline?.bp?.dia, end: attempt.final?.bp?.dia, render: (block: 'baseline' | 'final', value: string) => updateBloodPressure(block, 'dia', value) },
              { label: 'Borg dispneia', start: attempt.baseline?.borg?.dyspnea, end: attempt.final?.borg?.dyspnea, render: (block: 'baseline' | 'final', value: string) => updateBorg(block, 'dyspnea', value) },
              { label: 'Borg fadiga', start: attempt.baseline?.borg?.fatigue, end: attempt.final?.borg?.fatigue, render: (block: 'baseline' | 'final', value: string) => updateBorg(block, 'fatigue', value) },
            ].map((row) => (
              <tr key={`${label}-${row.label}`}>
                <td className="px-3 py-2 font-semibold text-slate-700">{row.label}</td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 px-2 py-1"
                    value={asInputValue(row.start)}
                    onChange={(event) => row.render('baseline', event.target.value)}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 px-2 py-1"
                    value={asInputValue(row.end)}
                    onChange={(event) => row.render('final', event.target.value)}
                  />
                </td>
                <td className="px-3 py-2 text-slate-600">{formatDelta(row.start, row.end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stopLines.length > 0 && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Paradas registradas</div>
          <div className="mt-2 text-sm text-slate-700">
            Tempo total parado: <span className="font-semibold">{attempt.stop_report?.total_stopped_fmt || '00:00'}</span>
          </div>
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            {stopLines.map((interval, index) => (
              <div key={`${label}-stop-${index}`}>
                {index + 1}. {interval.reason_label || 'Motivo não informado'} • {interval.from_fmt} → {interval.to_fmt} ({interval.duration_fmt})
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TC6ResultEditor({ payload, saving = false, onCancel, onSave }: TC6ResultEditorProps) {
  const [editablePayload, setEditablePayload] = useState<Tc6ResultPayload>(() => recalculateTc6Payload(payload));
  const [customInterpretation, setCustomInterpretation] = useState('');
  const [hasCustomInterpretation, setHasCustomInterpretation] = useState(false);

  useEffect(() => {
    const normalized = recalculateTc6Payload(payload);
    const automatic = buildTC6AutoInterpretation(normalized).trim();
    const stored = typeof payload?.report?.interpretation === 'string' ? payload.report.interpretation.trim() : '';

    setEditablePayload(normalized);
    if (stored && stored !== automatic) {
      setHasCustomInterpretation(true);
      setCustomInterpretation(payload?.report?.interpretation ?? '');
    } else {
      setHasCustomInterpretation(false);
      setCustomInterpretation('');
    }
  }, [payload]);

  const automaticInterpretation = useMemo(
    () => buildTC6AutoInterpretation(recalculateTc6Payload({ ...(editablePayload as Record<string, any>) })).trim(),
    [editablePayload],
  );

  const effectiveInterpretation = hasCustomInterpretation && customInterpretation.trim()
    ? customInterpretation.trim()
    : automaticInterpretation;

  const updatePayload = (updater: (current: Tc6ResultPayload) => Tc6ResultPayload) => {
    setEditablePayload((current) => recalculateTc6Payload(updater(current)));
  };

  const updateAttempt = (
    attemptKey: 'attempt1' | 'attempt2',
    updater: (current: Record<string, any>) => Record<string, any>,
  ) => {
    updatePayload((current) => ({
      ...current,
      [attemptKey]: updater(((current as Record<string, any>)[attemptKey] || {}) as Record<string, any>),
    }));
  };

  const handleSave = () => {
    const normalized = recalculateTc6Payload(editablePayload);
    const nextReport =
      effectiveInterpretation
        ? {
            ...(normalized.report && typeof normalized.report === 'object' ? normalized.report : {}),
            interpretation: effectiveInterpretation,
          }
        : undefined;

    onSave(
      nextReport
        ? { ...normalized, report: nextReport }
        : normalized,
    );
  };

  return (
    <div className="rounded-[24px] border border-amber-200 bg-amber-50/70 p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.25em] text-amber-700">Editor clínico do TC6</div>
          <div className="mt-1 text-sm text-amber-900">
            Ajuste a equação de referência, dados coletados e o texto final do laudo sem editar JSON bruto.
          </div>
        </div>
        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 shadow-sm">TC6 concluído</div>
      </div>

      <div className="mt-4 rounded-[24px] border border-amber-200 bg-white p-4">
        <label className="text-sm font-semibold text-slate-700">
          Equação de referência para o valor predito
          <select
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2"
            value={editablePayload.referenceEquation}
            onChange={(event) =>
              updatePayload((current) => ({
                ...current,
                referenceEquation: event.target.value as Tc6ReferenceKey,
              }))
            }
          >
            {TC6_REFERENCE_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 space-y-4">
        <TC6AttemptEditor
          label={editablePayload.attempt2 ? 'TC6 — 1ª tentativa' : 'TC6'}
          attempt={editablePayload.attempt1}
          selectedReferenceKey={editablePayload.referenceEquation}
          onChange={(updater) => updateAttempt('attempt1', updater)}
        />
        {editablePayload.attempt2 && (
          <TC6AttemptEditor
            label="TC6 — 2ª tentativa"
            attempt={editablePayload.attempt2}
            selectedReferenceKey={editablePayload.referenceEquation}
            onChange={(updater) => updateAttempt('attempt2', updater)}
          />
        )}
      </div>

      <div className="mt-4 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-sm font-semibold text-slate-900">Interpretação clínica do laudo</div>
        <textarea
          className="mt-3 min-h-[140px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm leading-6 text-slate-800"
          value={customInterpretation}
          onChange={(event) => {
            const nextValue = event.target.value;
            setCustomInterpretation(nextValue);
            setHasCustomInterpretation(nextValue.trim().length > 0);
          }}
          placeholder="Opcional: ajuste manualmente a interpretação. Se ficar em branco, o laudo usa o texto automático atualizado."
        />
        <div className="mt-2 text-xs text-slate-500">
          Se este campo ficar vazio, o laudo usa a interpretação automática recalculada.
        </div>
        {hasCustomInterpretation && (
          <button
            type="button"
            onClick={() => {
              setCustomInterpretation('');
              setHasCustomInterpretation(false);
            }}
            className="mt-3 inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Restaurar interpretação automática
          </button>
        )}

        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Prévia automática atual</div>
          <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
            {automaticInterpretation || 'Interpretação automática indisponível.'}
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancelar edição
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Salvando...' : 'Salvar ajustes do TC6'}
        </button>
      </div>
    </div>
  );
}
